import React from 'react';
import classNames from 'classnames';
import { Slide, Fade } from '@fiverr-private/transition';
import type { Types } from '@fiverr-private/theme';
import { Portal } from '@fiverr-private/ui_utils';
import { useIsomorphicLayoutEffect } from '@fiverr-private/hooks';
import { Container, Stack } from '@fiverr-private/layout_components';
import { useExitOnEscape } from '../../../hooks/useExitOnEscape';
import Overlay from '../../Overlay';
import { useDrawerContext } from '../DrawerContext';
import { DrawerContentProps } from '../types';
import { DIALOG_MAX_WIDTH, HEADER_APPROX_HEIGHT, SIDE_PADDING } from './constants';
import { fullScreenSupportsStyles } from './styles.ve.css';

const DrawerContent = ({
  position = 'right',
  initialFocusRef,
  size = 'md',
  closeOnOverlayClick = true,
  renderWhenClosed = false,
  isFullScreen = false,
  closeOnEscapeKey = true,
  children,
  overlayTestId,
  onAnimationStart,
  onAnimationEnd,
}: DrawerContentProps) => {
  const { isOpen, close } = useDrawerContext();

  const drawerRef = React.useRef<HTMLElement>(null);

  const hasOpened = React.useRef(false);

  React.useEffect(() => {
    if (drawerRef.current && isOpen && !hasOpened.current) {
      hasOpened.current = true;
      drawerRef.current.focus();
    }
  }, [isOpen]);

  useIsomorphicLayoutEffect(() => {
    if (drawerRef.current && isOpen) {
      if (initialFocusRef?.current) {
        initialFocusRef.current.focus();

        return;
      }

      drawerRef.current.focus();
    }
  }, [initialFocusRef, isOpen, drawerRef]);

  useExitOnEscape({
    enabled: closeOnEscapeKey,
    close,
    isOpen,
  });

  let dialogHeight: Types.FlexLayoutProps['height'] = '100%';
  let dialogWidth: Types.FlexLayoutProps['width'] = '100vw';

  if (isFullScreen) {
    if (['left', 'right'].includes(position)) {
      dialogWidth = {
        default: `100vw`,
        md: `calc(100vw - ${SIDE_PADDING})`,
        lg: '1100px',
      };
    } else {
      dialogHeight = { default: '100vh', md: `calc(100vh - ${HEADER_APPROX_HEIGHT})` };
    }
  }

  return (
    <Portal>
      <Container position="relative" zIndex="overlay">
        <Fade in={isOpen} delay={{ exit: 'fast2' }} unmountOnExit={!renderWhenClosed}>
          <Overlay
            zIndex="-1"
            dataTestId={overlayTestId}
            lockBodyScroll={isOpen}
            onClick={closeOnOverlayClick ? close : () => undefined}
          />
        </Fade>
        <Slide
          in={isOpen}
          direction={position}
          duration={{ enter: 'moderate2', exit: 'moderate1' }}
          unmountOnExit={!renderWhenClosed}
          onAnimationStart={onAnimationStart}
          onAnimationEnd={onAnimationEnd}
        >
          <Stack
            as="article"
            gap="0"
            direction="column"
            backgroundColor="white"
            aria-modal
            role="dialog"
            className={classNames({ [fullScreenSupportsStyles]: isFullScreen })}
            ref={drawerRef}
            tabIndex={-1}
            height={dialogHeight}
            maxWidth={isFullScreen ? dialogWidth : DIALOG_MAX_WIDTH[size]}
            borderBottomLeftRadius={position === 'top' ? 'xl' : undefined}
            borderBottomRightRadius={position === 'top' ? 'xl' : undefined}
            borderTopLeftRadius={position === 'bottom' ? 'xl' : undefined}
            borderTopRightRadius={position === 'bottom' ? 'xl' : undefined}
            borderRadius={isFullScreen ? 'none' : undefined}
          >
            {children}
          </Stack>
        </Slide>
      </Container>
    </Portal>
  );
};

DrawerContent.displayName = 'DrawerContent';

export default DrawerContent;
